import { useContext, useMemo } from 'react';
import _ from 'lodash';
import cx from 'classnames';
import { Button } from 'reactstrap';
import styled from 'styled-components';
import { PanelSection } from '../SidePanel';
import { SquareSwatch } from '../right-menu/SquareSwatch';
import LayersIcon from '../icons/LayersIcon';
import { darkenColor, getPaintColor } from '../../utils/colors';
import { useToggleLayerVisibility } from './utils/visibility';
import { PANEL_INNER_HEIGHT } from './constants';
import { FieldsPreviewContext, SectionHeader } from './components';
import { MapContext } from '../../contexts/MapContext';
import { useUnitContext, useUnits } from '../../contexts/UnitContext';
import { AREA_UNITS, DISTANCE_UNITS } from '../../constants/units';
import {
  defaultLayerUnits,
  filterFeaturesByType,
  getLayerArea,
  getLayerLength,
  layerHasData,
} from '../../utils/layers';
import { asDynamicDecimal, asQuantityWithUnit } from '../../utils/formatters';

// Indented to match the indentation of the crop name
const IndentedLayerDetails = styled.div`
  line-height: 1.4;
  margin-left: 28px;
`;

const ROW_HEIGHT = 40;
const ROW_MARGIN = 8;

const Row = ({ layer, toggle }) => {
  const { layerId, displayName, stats, shown } = layer;
  const color = getPaintColor(layer);

  return (
    <Button
      className={cx('p-0 w-100', { 'text-muted': !shown })}
      color="text"
      onClick={toggle}
      key={layerId}
      size="sm"
      style={{ height: ROW_HEIGHT, marginBottom: ROW_MARGIN }}
    >
      <div className="d-flex align-items-center mb-1">
        <SquareSwatch
          className="mr-2"
          color={color}
          borderColor={darkenColor(color)}
        />
        <strong className="ml-2 text-truncate">{displayName}</strong>
      </div>
      {stats && (
        <IndentedLayerDetails className="text-left text-truncate">
          {stats}
        </IndentedLayerDetails>
      )}
    </Button>
  );
};

// Returns `layers` with an additional `stats` string about area/length/points for each
const useLayersWithStats = () => {
  const {
    layers: { sortedLayers },
  } = useContext(MapContext);
  const { units } = useUnitContext();

  const { transformToUnit: transformArea } = useUnits(AREA_UNITS);
  const { transformToUnit: transformLength } = useUnits(DISTANCE_UNITS);

  return useMemo(
    () =>
      sortedLayers
        // include layers with data or that have not been loaded yet
        .filter((layer) => layerHasData(layer) || layer.hasRemote)
        .map((layer) => {
          const { data, units: { unitSpec } = {} } = layer;
          const layerUnit = unitSpec?.[units || 'default']?.display;

          const polygons = filterFeaturesByType(data, 'Polygon');
          const lines = filterFeaturesByType(data, 'LineString');
          const points = filterFeaturesByType(data, 'Point');

          const area = getLayerArea(layer, polygons);
          const length = getLayerLength(layer, lines);

          const formattedArea =
            area &&
            `${asDynamicDecimal(transformArea(area))} ${
              layerUnit ?? defaultLayerUnits.polygon
            }`;

          const formattedLength =
            length &&
            `${asDynamicDecimal(transformLength(length))} ${
              layerUnit ?? defaultLayerUnits.lineString
            }`;

          const formattedPoints =
            points.features.length &&
            asQuantityWithUnit('points', 'point')(points.features.length);

          return {
            ...layer,
            stats:
              formattedArea ??
              formattedLength ??
              (formattedPoints || '0 features'),
          };
        }),
    [sortedLayers, transformArea, transformLength, units],
  );
};

export const useLayersInfo = () => {
  const layersWithStats = useLayersWithStats();

  const toggleLayerVisibility = useToggleLayerVisibility();

  const { isPrintMode, panelVisibility, togglePanelVisibility } = useContext(
    FieldsPreviewContext,
  );

  const filteredLayers = isPrintMode
    ? layersWithStats.filter((layer) => layer.shown)
    : layersWithStats;

  const rowsPerPanel = Math.floor(
    PANEL_INNER_HEIGHT / (ROW_HEIGHT + ROW_MARGIN),
  );

  const chunks = _.chunk(filteredLayers, rowsPerPanel);

  const header = (
    <SectionHeader
      onClick={() => togglePanelVisibility('layers')}
      title="Layers"
      icon={<LayersIcon />}
    />
  );

  if (!panelVisibility.layers) {
    if (isPrintMode) return [];

    return [
      <PanelSection className="opacity-50" key="layers-hidden">
        {header}
      </PanelSection>,
    ];
  }

  return chunks.map((chunk, i) => (
    <PanelSection
      $hideBottomBorder={i !== chunks.length - 1}
      key={chunk.map(({ layerId }) => layerId).join('-')}
    >
      {i === 0 && header}

      {chunk.map((layer) => (
        <Row
          layer={layer}
          key={layer.layerId}
          toggle={() => toggleLayerVisibility(layer.layerId)}
        />
      ))}
    </PanelSection>
  ));
};
